import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, CheckCheck, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { notificationPath } from "@/lib/notification-target";
import { categoryMeta, groupNotifications } from "@/lib/notification-meta";
import {
  useBatchNotifications,
  useMarkAllRead,
  useNotifications,
  useNotificationSummary,
  type Notification,
} from "@/hooks/notifications/use-notifications";

import { NotificationRow } from "./notification-row";

export type NotificationView = "all" | "unread" | "critical";

interface Props {
  /** `popover`: o sino (compacto, 50 itens). `page`: o centro de notificações (mais espaço, resolvidas à escolha). */
  variant: "popover" | "page";
  /** Chamado ao abrir uma notificação (o sino fecha-se). */
  onNavigate?: () => void;
  className?: string;
}

const VIEWS: { id: NotificationView; label: string }[] = [
  { id: "all", label: "Todas" },
  { id: "unread", label: "Por ler" },
  { id: "critical", label: "Críticas" },
];

/**
 * A lista de notificações com filtros, agrupamento e acções em lote — a mesma no sino e na página.
 *
 * Ordenada por prioridade (por ler primeiro e, dentro delas, o mais grave) e agrupada em «Requer
 * atenção», «Novas» e «Anteriores». Os filtros vêm do resumo do servidor: só se oferecem categorias
 * com notificações, com a contagem das por ler.
 */
export function NotificationCenter({ variant, onNavigate, className }: Props) {
  const navigate = useNavigate();
  const isPage = variant === "page";
  const [view, setView] = useState<NotificationView>("all");
  const [category, setCategory] = useState<string | undefined>();
  const [showResolved, setShowResolved] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, refetch } = useNotifications({
    limit: isPage ? 100 : 50,
    sort: "priority",
    category,
    unreadOnly: view === "unread",
    severity: view === "critical" ? "critical" : undefined,
    includeResolved: isPage && showResolved,
  });
  const { data: summary } = useNotificationSummary();
  const batch = useBatchNotifications();
  const markAll = useMarkAllRead();

  const items = useMemo(() => data ?? [], [data]);
  const groups = useMemo(() => groupNotifications(items), [items]);
  const criticalUnread = summary?.by_severity.critical ?? 0;
  const unread = summary?.unread ?? 0;
  const categories = useMemo(() => (summary?.by_category ?? []).filter((c) => c.total > 0), [summary]);
  const selecting = selected.size > 0;

  const open = useCallback(
    (n: Notification) => {
      if (!n.is_read) batch.mutate({ ids: [n.id], action: "read" });
      const path = notificationPath(n);
      if (path) {
        onNavigate?.();
        navigate(path);
      }
    },
    [batch, navigate, onNavigate],
  );

  const toggleSelect = useCallback((id: string, next: boolean) => {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  }, []);

  const runBatch = (action: "read" | "unread" | "delete") => {
    const ids = [...selected];
    batch.mutate({ ids, action }, { onSuccess: () => setSelected(new Set()) });
  };

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      {/* Vistas + categorias */}
      <div className="space-y-2 border-b border-border/60 px-4 py-2.5">
        <div className="flex items-center gap-1" role="tablist" aria-label="Filtrar por estado">
          {VIEWS.map((v) => {
            const count = v.id === "unread" ? unread : v.id === "critical" ? criticalUnread : undefined;
            const active = view === v.id;
            return (
              <button
                key={v.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setView(v.id)}
                className={cn(
                  "flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[12px] transition-colors focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-primary/10",
                  active ? "bg-foreground/6 font-medium text-foreground" : "text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground",
                )}
              >
                {v.label}
                {count !== undefined && count > 0 && (
                  <span className={cn("tabular-nums", v.id === "critical" ? "font-medium text-destructive" : "text-muted-foreground")}>{count}</span>
                )}
              </button>
            );
          })}
          {isPage && (
            <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground">
              <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} className="h-3.5 w-3.5 accent-primary" />
              Mostrar resolvidas
            </label>
          )}
        </div>

        {categories.length > 1 && (
          <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-none" role="group" aria-label="Filtrar por categoria">
            {categories.map((c) => {
              const Icon = categoryMeta(c.category).icon;
              const active = category === c.category;
              return (
                <button
                  key={c.category}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setCategory(active ? undefined : c.category)}
                  className={cn(
                    "flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[11px] transition-colors",
                    active ? "border-primary/40 bg-primary/5 text-foreground" : "border-border/60 text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground",
                  )}
                >
                  <Icon className="h-3 w-3" strokeWidth={1.75} />
                  {c.label}
                  {c.unread > 0 && <span className="tabular-nums text-foreground/70">{c.unread}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Barra de acções em lote */}
      {selecting ? (
        <div role="toolbar" aria-label="Ações em lote" className="flex items-center gap-2 border-b border-border/60 bg-foreground/3 px-4 py-2 text-[12px]">
          <span className="tabular-nums text-foreground">{selected.size} selecionada{selected.size !== 1 ? "s" : ""}</span>
          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => runBatch("read")} disabled={batch.isPending}>
              Marcar lidas
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => runBatch("unread")} disabled={batch.isPending}>
              Por ler
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px] text-destructive hover:text-destructive" onClick={() => runBatch("delete")} disabled={batch.isPending}>
              Eliminar
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px] text-muted-foreground" onClick={() => setSelected(new Set())}>
              Limpar
            </Button>
          </div>
        </div>
      ) : (
        unread > 0 && (
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-1.5 text-[11px] text-muted-foreground">
            <span className="tabular-nums">{unread} por ler</span>
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-foreground/4 hover:text-foreground"
            >
              {markAll.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" strokeWidth={1.75} />}
              Marcar tudo como lido
            </button>
          </div>
        )
      )}

      {/* Lista */}
      <div className={cn("min-h-0 overflow-y-auto overscroll-contain", isPage ? "max-h-[calc(100vh-18rem)] min-h-64" : "max-h-[min(440px,60vh)]")}>
        {isLoading ? (
          <div className="space-y-3 p-4" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-4 w-4 rounded" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/5" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div role="alert" className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-[13px] text-muted-foreground">Não foi possível carregar as notificações.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            {view === "all" && !category ? <Bell className="h-5 w-5 text-muted-foreground/30" strokeWidth={1.5} /> : <BellOff className="h-5 w-5 text-muted-foreground/30" strokeWidth={1.5} />}
            <p className="text-[13px] text-muted-foreground">
              {view === "critical" ? "Nada de crítico" : view === "unread" ? "Tudo lido" : category ? "Nada nesta categoria" : "Sem notificações"}
            </p>
            <p className="max-w-[260px] text-xs text-muted-foreground/60">
              {view === "all" && !category ? "Os avisos da tua instalação aparecem aqui quando houver algo a tratar." : "Não há nada que corresponda a este filtro."}
            </p>
            {(view !== "all" || category) && (
              <Button variant="ghost" size="sm" className="mt-1 h-7 text-[12px]" onClick={() => (setView("all"), setCategory(undefined))}>
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          groups.map((g) => (
            <section key={g.key} aria-label={g.label}>
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border/60 bg-popover px-4 py-1.5">
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">{g.label}</p>
                <span className="text-[10px] tabular-nums text-muted-foreground/70">{g.items.length}</span>
              </div>
              <div className="divide-y divide-border/40">
                {g.items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    compact={!isPage}
                    selected={selected.has(n.id)}
                    selecting={selecting}
                    onOpen={open}
                    onSelect={toggleSelect}
                    onToggleRead={(x) => batch.mutate({ ids: [x.id], action: x.is_read ? "unread" : "read" })}
                    onArchive={(x) => batch.mutate({ ids: [x.id], action: "delete" })}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
